import React, { useState, useEffect } from 'react';
import { 
  Share2, Package, Settings, Check, X, Eye, Trash2,
  DollarSign, TrendingUp, Users, ShoppingCart, Search
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

const AffiliateAdmin = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, settingsRes] = await Promise.all([
        api.get('/admin/affiliate/stats'),
        api.get('/admin/affiliate/settings')
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);

      if (activeTab === 'products') {
        const res = await api.get(`/admin/affiliate/products?status=${statusFilter}`);
        setProducts(res.data.products);
      } else if (activeTab === 'sales') {
        const res = await api.get('/admin/affiliate/sales?limit=100');
        setSales(res.data.sales);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const approveProduct = async (productId) => {
    try {
      await api.put(`/admin/affiliate/products/${productId}/approve`);
      toast.success('Product approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const rejectProduct = async (productId) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      await api.put(`/admin/affiliate/products/${productId}/reject?reason=${reason || ''}`);
      toast.success('Product rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const deleteProduct = async (productId) => {
    if (!confirm('Delete this product?')) return;
    try {
      await api.delete(`/admin/affiliate/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const payCommission = async (saleId) => {
    try {
      await api.put(`/admin/affiliate/sales/${saleId}/pay-commission`);
      toast.success('Commission paid');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pay commission');
    }
  };

  const updateSettings = async () => {
    try {
      await api.put('/admin/affiliate/settings', settings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="affiliate-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Share2 className="w-8 h-8 text-primary" />
              Affiliate Management
            </h1>
            <p className="text-muted-foreground">Manage affiliate products and commissions</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Package} label="Total Products" value={stats.total_products} color="blue" />
            <StatCard icon={ShoppingCart} label="Total Sales" value={stats.total_sales} color="green" />
            <StatCard icon={DollarSign} label="Total Revenue" value={`₹${stats.total_revenue}`} color="primary" />
            <StatCard icon={DollarSign} label="Pending Commission" value={`₹${stats.pending_commission}`} color="yellow" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6">
          {['products', 'sales', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 capitalize transition-colors ${
                activeTab === tab 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex gap-4 mb-6">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4">Product</th>
                    <th className="text-left p-4">Vendor</th>
                    <th className="text-left p-4">Price</th>
                    <th className="text-left p-4">Commission</th>
                    <th className="text-left p-4">Stats</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id} className="border-t border-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.images?.[0] && (
                            <img src={getImageUrl(product.images[0])} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <p className="text-xs text-muted-foreground">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm">{product.vendor_name}</td>
                      <td className="p-4">₹{product.price}</td>
                      <td className="p-4 text-green-500">
                        {product.commission_type === 'percentage' 
                          ? `${product.commission_value}%` 
                          : `₹${product.commission_value}`}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        <span>{product.views || 0} views</span> • <span>{product.sales || 0} sales</span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          product.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          product.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-red-500/20 text-red-500'
                        }`}>
                          {product.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {product.status === 'pending' && (
                            <>
                              <button
                                onClick={() => approveProduct(product.id)}
                                className="p-2 bg-green-500/20 text-green-500 rounded hover:bg-green-500/30"
                                title="Approve"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => rejectProduct(product.id)}
                                className="p-2 bg-red-500/20 text-red-500 rounded hover:bg-red-500/30"
                                title="Reject"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => deleteProduct(product.id)}
                            className="p-2 hover:bg-red-500/20 text-red-500 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4">Product</th>
                  <th className="text-left p-4">Buyer</th>
                  <th className="text-left p-4">Referrer</th>
                  <th className="text-left p-4">Price</th>
                  <th className="text-left p-4">Commission</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(sale => (
                  <tr key={sale.id} className="border-t border-white/5">
                    <td className="p-4">{sale.product_name}</td>
                    <td className="p-4 text-sm">{sale.buyer_id?.slice(0, 8)}...</td>
                    <td className="p-4 text-sm">{sale.referrer_id?.slice(0, 8) || '-'}...</td>
                    <td className="p-4">₹{sale.price}</td>
                    <td className="p-4 text-green-500">₹{sale.commission_amount}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        sale.status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                      }`}>
                        {sale.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(sale.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      {sale.status === 'pending' && sale.referrer_id && (
                        <button
                          onClick={() => payCommission(sale.id)}
                          className="px-3 py-1 bg-green-500/20 text-green-500 rounded hover:bg-green-500/30 text-sm"
                        >
                          Pay Commission
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div className="glass-card p-6 max-w-2xl">
            <h3 className="text-lg font-semibold mb-6">Affiliate Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium">Enable Affiliate Marketplace</label>
                  <p className="text-sm text-muted-foreground">Allow affiliate product listings</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.is_enabled}
                  onChange={(e) => setSettings({...settings, is_enabled: e.target.checked})}
                  className="toggle"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium">Require Approval</label>
                  <p className="text-sm text-muted-foreground">Products need admin approval</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.require_approval}
                  onChange={(e) => setSettings({...settings, require_approval: e.target.checked})}
                  className="toggle"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium">Allow Member Listings</label>
                  <p className="text-sm text-muted-foreground">Activated members can list products</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.allow_member_listings}
                  onChange={(e) => setSettings({...settings, allow_member_listings: e.target.checked})}
                  className="toggle"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Default Commission Type</label>
                  <select
                    value={settings.default_commission_type}
                    onChange={(e) => setSettings({...settings, default_commission_type: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Default Commission Value</label>
                  <input
                    type="number"
                    value={settings.default_commission_value}
                    onChange={(e) => setSettings({...settings, default_commission_value: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Minimum Payout Amount (₹)</label>
                <input
                  type="number"
                  value={settings.min_payout_amount}
                  onChange={(e) => setSettings({...settings, min_payout_amount: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              
              <button onClick={updateSettings} className="btn-primary mt-4">
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/20',
    blue: 'text-blue-500 bg-blue-500/20',
    green: 'text-green-500 bg-green-500/20',
    yellow: 'text-yellow-500 bg-yellow-500/20'
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateAdmin;
